import { wait } from '@wwe-portal/ui/lib/timer';

import {
  getPaymentStore,
  Payment,
  PaymentContext,
} from '@wwe-portal/web/providers/stores/payment-store';
import { OfferData } from '@wwe-portal/entities/offer.type';
import {
  BaseDialogState,
  useProcessingDialog,
} from '@wwe-portal/web/hooks/payment/processing-dialog';
import { VERIFY_DURATION_MS } from '@wwe-portal/web/utils/constants/offer';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { useStore } from 'zustand';
import { useNotifyPaymentError } from './use-notify-payment-error';
import { useInvalidateOffer } from './use-invalidate-offer';

export const useXsollaPayment = (
  source: string,
  {
    onPaymentSuccess,
    onCloseSuccessDialog,
    onDescribePayment,
    progressDialogEnabled = true,
  }: {
    onPaymentSuccess: (data: Payment) => void;
    onCloseSuccessDialog?: (data: {
      offerData: OfferData;
      context: PaymentContext;
    }) => void;
    onDescribePayment?: (offerData: OfferData) => BaseDialogState | undefined;
    progressDialogEnabled?: boolean;
  }
) => {
  const t = useTranslations();
  const invalidate = useInvalidateOffer();

  const notifyPaymentError = useNotifyPaymentError();

  const {
    showProcess,
    showSuccess,
    modal: processingModal,
  } = useProcessingDialog({
    progressData: {
      title: t('payment_title_load'),
      description: '',
      duration: VERIFY_DURATION_MS,
    },
    successData: {
      title: t('payment_title_successful'),
      description: t('payment_description_successful'),
    },
  });

  const handleError = async (
    error: unknown,
    data: { offerData: OfferData; context: PaymentContext }
  ) => {
    notifyPaymentError(data, error);
    getPaymentStore().getState().clearPayments();
  };

  const handlePaymentSuccess = async (successPayment: Payment) => {
    if (progressDialogEnabled) {
      showProcess();
    }
    onPaymentSuccess(successPayment);

    if (progressDialogEnabled) {
      await Promise.all([wait(VERIFY_DURATION_MS), invalidate()]);
      showSuccess(
        onDescribePayment?.(successPayment.offerData) ?? undefined,
        () => {
          onCloseSuccessDialog?.(successPayment);
        }
      );
    }
  };

  const startPayment = async ({
    offerData,
    context,
    isSkipValidation,
  }: {
    offerData: OfferData;
    context: PaymentContext;
    isSkipValidation?: boolean;
  }) => {
    try {
      const paymentStore = getPaymentStore();

      await paymentStore.getState().startPayment(
        {
          offerData,
          source,
          context,
        },
        isSkipValidation
      );
    } catch (error) {
      handleError(error, { offerData, context });
    }
  };

  useEffect(() => {
    const store = getPaymentStore();

    const unsubscribe = store.subscribe(
      (state) => state.successPayment,
      (successPayment) => {
        if (successPayment && successPayment.source === source) {
          store.getState().clearSuccessPayment();
          handlePaymentSuccess(successPayment);
        }
      },
      {
        fireImmediately: true,
      }
    );
    return () => {
      unsubscribe();
    };
  }, []);

  return {
    processingModal,
    startPayment,
  };
};

export const usePendingPayment = () => {
  return useStore(getPaymentStore(), (state) => state.pendingPayment);
};

export const useIsPendingPayment = (offerData?: OfferData) => {
  const pendingPayment = usePendingPayment();
  return pendingPayment?.offerData?.OffersID === offerData?.OffersID;
};
